/*
 * The skins shop (ENG-823): what SongShake has for sale, from
 * `GET /public/skins` (backend ENG-822, schema `SkinShop`):
 *
 *   { items: [{ itemType: 'skin', skin: SkinsSkin, forSale: true, priceCents,
 *               editionSize?, editionsIssued, nextEdition?, soldOut, owned? }] }
 *
 * `owned` comes back only with a session. Earn-only skins are left out by the
 * server; sold-out ones stay in. Skins are SongShake's, so nothing here names
 * or sends a band.
 *
 * Each item is read by its `itemType`: `skin`, or an FX item (ENG-815/816),
 * `vibe_set` or `boost_animation`, whose payload is in `vibeSet` /
 * `boostAnimation` instead of `skin` (fxItems.js). Every normalized item
 * keeps its payload under `skin`, so the shop and the shelf read them alike.
 * An item of a type this build doesn't know is skipped, not shown broken, and
 * so is an FX item without a package.
 *
 * A price of 0 is a free skin (ENG-825): it is kept as 0, shown as "Free",
 * and "Get it" gets it through the same purchase as a paid one (ENG-827).
 * `priceCents` is passed through as sent; it goes back as
 * `expected_price_cents`.
 */
import config from '../config.js';
import { fetchWrapper } from './fetchWrapper.js';
import { canBuyOffer, normalizeSkinOffer } from './skinTryOn.js';
import { FX_ITEM_FIELD } from './fxItems.js';

/** The shop's tabs: everything, or one skin type (also the `?type=` values). */
export const SHOP_TYPES = ['all', 'now_playing_card', 'name_paint', 'vibe_set', 'boost_animation'];

/** One `skin` item: the offer's fields (skinTryOn.js) plus `owned`. */
const normalizeSkinItem = (input) => {
  const offer = normalizeSkinOffer({ ...input, forSale: input.forSale !== false });
  if (!offer) return null;
  return {
    itemType: 'skin',
    id: offer.skin.id,
    ...offer,
    owned: input.owned === true,
  };
};

/**
 * One FX item (`vibe_set` or `boost_animation`): its payload read like a skin,
 * typed by `itemType`; null without an `fxPackageId`.
 */
const normalizeFxItem = (itemType) => (input) => {
  const payload = input[FX_ITEM_FIELD[itemType]];
  if (!payload || typeof payload !== 'object') return null;
  const item = normalizeSkinItem({ ...input, skin: { ...payload, type: itemType } });
  if (!item?.skin.fxPackageId) return null;
  return { ...item, itemType };
};

const ITEM_TYPES = {
  skin: normalizeSkinItem,
  vibe_set: normalizeFxItem('vibe_set'),
  boost_animation: normalizeFxItem('boost_animation'),
};

/** One `SkinShopItem`, checked; null for an unknown `itemType` or a broken item. */
export const normalizeShopItem = (input) => {
  if (!input || typeof input !== 'object') return null;
  const normalize = Object.prototype.hasOwnProperty.call(ITEM_TYPES, input.itemType) ? ITEM_TYPES[input.itemType] : null;
  return normalize ? normalize(input) : null;
};

/** The `SkinShop` payload's items, checked; null when it is not one. */
export const normalizeSkinShop = (input) => {
  if (!input || typeof input !== 'object' || !Array.isArray(input.items)) return null;
  return input.items.map(normalizeShopItem).filter(Boolean);
};

/** The tab for a `?type=` value: one of SHOP_TYPES, 'all' for anything else. */
export const shopType = (value) => (SHOP_TYPES.includes(value) ? value : 'all');

/** The items shown under `type` ('all', a skin type or an FX item type). */
export const itemsOfType = (items, type) => {
  if (!items) return [];
  if (type === 'all') return items;
  return items.filter((item) => item.skin.type === type);
};

/** Whether "Get it" can buy this item: not owned, for sale (free included) and not sold out. */
export const canBuyShopItem = (item) => Boolean(item && !item.owned && canBuyOffer(item));

/**
 * Fetches the whole catalog. The fetch wrapper sends the session when there is
 * one, so `owned` comes back. Rejects with the fetchWrapper error.
 */
export const fetchSkinShop = async () => {
  const data = await fetchWrapper.get(`${config.apiBaseUrl}/public/skins`);
  const items = normalizeSkinShop(data);
  if (!items) return Promise.reject({ status: 500, code: null, message: 'malformed skin shop' });
  return items;
};

/** How rare each rarity is, for ordering (ENG-894). */
const RARITY_RANK = { common: 0, rare: 1, epic: 2, legendary: 3 };
const rarityRank = (item) => RARITY_RANK[item?.skin?.rarity] ?? 0;

/** Whether an item is a limited edition (a lifetime cap on copies). */
export const isLimitedItem = (item) => Number.isSafeInteger(item?.editionSize) && item.editionSize > 0;

/** Whether an item can headline the shop: for sale, not owned and not sold out. */
const canFeature = (item) => Boolean(item && item.forSale && !item.owned && !item.soldOut);

/**
 * The item the shop leads with (ENG-894): "Featured drop" in the hero and the
 * drops band. There is no `featured` flag on `GET /public/skins` yet, so it is
 * picked here, the one place a backend flag would replace: of the items for
 * sale that the viewer doesn't own and that aren't sold out, the rarest, then
 * limited editions before unlimited ones, then catalog order. Null when
 * nothing qualifies.
 */
export const pickFeaturedItem = (items) => {
  if (!Array.isArray(items)) return null;
  let best = null;
  let bestIndex = -1;
  items.forEach((item, index) => {
    if (!canFeature(item)) return;
    if (!best) {
      best = item;
      bestIndex = index;
      return;
    }
    const byRarity = rarityRank(item) - rarityRank(best);
    const byLimited = Number(isLimitedItem(item)) - Number(isLimitedItem(best));
    if (byRarity > 0 || (byRarity === 0 && byLimited > 0) || (byRarity === 0 && byLimited === 0 && index < bestIndex)) {
      best = item;
      bestIndex = index;
    }
  });
  return best;
};

/** The kinds of secondary drop, in the order they are picked. */
export const DROP_KINDS = ['leaving', 'sale', 'limited'];

const DROP_MATCH = {
  leaving: (item) => item.availableUntil instanceof Date,
  sale: (item) => Boolean(item.sale),
  limited: (item) => isLimitedItem(item),
};

/** The items of one drop kind, best first: soonest to leave, sale ending first, fewest left. */
const dropCandidates = (items, kind) => {
  const matching = items.filter((item) => canFeature(item) && DROP_MATCH[kind](item));
  const key = {
    leaving: (item) => item.availableUntil.getTime(),
    sale: (item) => Date.parse(item.sale.endsAt),
    limited: (item) => item.editionSize - (item.editionsIssued ?? 0),
  }[kind];
  return matching
    .map((item, index) => ({ item, index, key: key(item) }))
    .sort((a, b) => (a.key - b.key) || (a.index - b.index))
    .map(({ item }) => item);
};

/**
 * The secondary drops beside the featured item in the wide layout (ENG-894):
 * `[{ kind, item }]`, at most `count`. Owned, sold-out and not-for-sale items
 * and the featured one are skipped. Each kind gets a slot in turn, Leaving
 * soon (`availableUntil`), then On sale (`sale`), then Limited run (editions
 * left); an item shows once, under the first kind it fits. When a kind has
 * nothing, the next fills its slot; when slots are left after one of each,
 * the kinds are walked again for their next item.
 */
export const pickSecondaryDrops = (items, featured = null, count = 2) => {
  if (!Array.isArray(items) || count <= 0) return [];
  const queues = DROP_KINDS.map((kind) => ({
    kind,
    items: dropCandidates(items, kind).filter((item) => item.id !== featured?.id),
  }));
  const picked = [];
  const used = new Set();
  let added = true;
  while (picked.length < count && added) {
    added = false;
    for (const queue of queues) {
      if (picked.length >= count) break;
      const next = queue.items.find((item) => !used.has(item.id));
      if (next) {
        used.add(next.id);
        picked.push({ kind: queue.kind, item: next });
        added = true;
      }
    }
  }
  return picked;
};

/** How many items each tab holds: `{ all, now_playing_card, … }`. */
export const shopTypeCounts = (items) => Object.fromEntries(
  SHOP_TYPES.map((type) => [type, itemsOfType(items, type).length]),
);
